import {
  CSSProperties,
  FC,
  MouseEventHandler,
  useCallback,
  useMemo,
  useState,
} from 'react'

import styled from '@emotion/styled'
import { Text } from '@hhgtech/hhg-components/mantine'
import { MediaQueries, theme } from '@hhgtech/hhg-components/misc'
import { Box, clsx, ScrollArea } from '@mantine/core'
import innertext from 'innertext'

import { formatArticle } from 'api/dataTransform'
import { getHelloSitesUrl } from 'api/utils/paths'
import { CardArticle } from 'components/atoms/CardArticleV2'
import { CardArticleAuthorName } from 'components/atoms/CardArticleV2/CardArticleAuthor'
import { NextImageWrap } from 'components/atoms/imageWrap'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath, DEFAULT_IMG } from 'interfaces/constants'
import { TrimesterAddition } from 'interfaces/types/DueDate'
import { getAuthorFromArticleApi } from 'utils/author'

export const StyledCardArticle = styled(CardArticle)`
  &.cardArticle {
    display: block;
  }
  .cardArticleImageWrap {
    display: none;
  }
`

export type LastestPregnancyProps = Partial<TrimesterAddition['categories']> & {
  className?: string
  style?: CSSProperties
}

export const LastestPregnancy: FC<LastestPregnancyProps> = (props) => {
  const { title, description, categories: catsProps, className, style } = props

  const { locale } = useRouterExtend()

  const [active, setActive] = useState(0)

  const categories = useMemo(() => {
    // https://hhgdev.atlassian.net/browse/HHD-6399
    return catsProps?.slice(0, 4)
  }, [catsProps])

  const onActive = useCallback(
    (index: number) =>
      (() => {
        setActive(index)
      }) as MouseEventHandler<HTMLDivElement>,
    [],
  )

  const currentDomain = useMemo(() => getHelloSitesUrl(locale), [locale])

  if (!categories || !categories.length) {
    return null
  }

  return (
    <Box className={clsx(className, 'LastestPregnancy')} style={style}>
      <Box className="LastestPregnancy-head pt-6 px-4 pb-3 md:pt-0 md:px-0">
        <Text as="h3" size="s1" color={theme.colors.gray800} weight="semiBold">
          {title}
        </Text>
        {!!description && (
          <Text
            as="p"
            size="p4"
            color={theme.colors.gray600}
            className="mt-3 md:mt-2"
          >
            {description}
          </Text>
        )}
      </Box>
      <Box className="LastestPregnancy-body flex flex-col gap-[2px] md:flex-row md:gap-2">
        {categories.map((cat, index) => {
          const isActive = active === index
          const { icon, image, cat_name, articles } = cat

          return (
            <Box
              key={`${cat.term_id}`}
              onClick={onActive(index)}
              className={clsx(
                'LastestPregnancy-item',
                'relative flex flex-col',
                'p-4 bg-pink100 md:rounded-lg transition-all ease-in-out duration-300',
                {
                  'max-md:py-2 cursor-pointer h-[48px] md:h-[360px]': !isActive,
                  'flex-1 min-h-0 md:min-w-0 h-[672px] md:h-[360px]': isActive,
                },
              )}
            >
              <Box
                className={clsx(
                  'LastestPregnancy-item_head',
                  'flex items-center gap-2',
                  {
                    'flex-1 min-w-0': !isActive,
                  },
                )}
                sx={{
                  [`${MediaQueries.lgPcUp}`]: isActive
                    ? undefined
                    : {
                        writingMode: 'vertical-rl',
                      },
                }}
              >
                <Box className="LastestPregnancy-item_img relative w-8 h-8 bg-neutral50 rounded-[50%] overflow-hidden">
                  <NextImageWrap
                    src={icon || image || ''}
                    alt={cat_name}
                    fill
                    className="!inset-1"
                  />
                </Box>
                <Text
                  as="p"
                  size="s3"
                  className="LastestPregnancy-item_title flex-1 min-w-0 min-h-0 !text-gray800 !text-[16px] md:!text-[18px]"
                  sx={{
                    [`${MediaQueries.lgPcUp}`]: isActive
                      ? undefined
                      : {
                          transform: 'rotate(180deg)',
                          transformOrigin: 'center',
                        },
                  }}
                >
                  {cat_name}
                </Text>
              </Box>
              <Box
                className={clsx('LastestPregnancy-item_body flex-1 min-h-0', {
                  hidden: !isActive,
                  'mt-4': isActive,
                })}
              >
                <ScrollArea
                  type="always"
                  scrollbarSize={8}
                  styles={{
                    thumb: {
                      backgroundColor: theme.colors.gray200,
                    },
                  }}
                  className={clsx(
                    'LastestPregnancy-item_scroll',
                    'p-4 bg-white rounded-lg h-full w-full',
                    {
                      'hidden pointer-events-none max-md:h-0': !isActive,
                      'opacity-100 w-full': isActive,
                    },
                  )}
                >
                  {articles.map((post) => {
                    const article = formatArticle(post)
                    const author = getAuthorFromArticleApi({
                      article: post,
                      locale,
                      version: 'short',
                    })

                    return (
                      <StyledCardArticle
                        key={article.id.toString()}
                        type="horizontal"
                        item={{
                          ID: parseInt(`${article.id}`),
                          image: (
                            <NextImageWrap
                              src={article.featureImage || DEFAULT_IMG}
                              fill
                              alt={article.postName}
                            />
                          ),

                          permalink: article.permalink,
                          title: innertext(
                            article.postTitle || article.postName || '',
                          ),
                          category: article.category,
                          author: {
                            name: <CardArticleAuthorName {...author} />,
                            permalink: author.avatar_permalink,
                            avatar: author.avatar,
                            indicator: author.indicator,
                            avatarAlt: author.avatar_alt,
                          },
                        }}
                        itemTracking={{
                          image: {
                            'data-event-category': 'Health Tool',
                            'data-event-action':
                              'Due Date - Helpful Tips - Click Latest Article',
                            'data-event-label': `${currentDomain}${basePath}${article.permalinkDecode}`,
                          },
                          title: {
                            'data-event-category': 'Health Tool',
                            'data-event-action':
                              'Due Date - Helpful Tips - Click Latest Article',
                            'data-event-label': `${currentDomain}${basePath}${article.permalinkDecode}`,
                          },
                          category: {
                            'data-event-category': 'Health Tool',
                            'data-event-action':
                              'Due Date - Helpful Tips - Click Latest Article Category',
                            'data-event-label': `${currentDomain}${basePath}${article.category.permalinkDecode}`,
                          },
                          authorAvatar: {
                            'data-event-category': 'Health Tool',
                            'data-event-action':
                              'Due Date - Helpful Tips - Click Latest Article - Click See Doctor',
                            'data-event-label': `${currentDomain}${basePath}${author.avatar_permalink}`,
                          },
                        }}
                        bookmarkButtonProps={{
                          style: { display: 'none' },
                        }}
                        className="py-2 first:pt-0 first:border-none last:pb-0 border-solid border-t border-t-neutral100"
                      />
                    )
                  })}
                </ScrollArea>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
